/**
 * Shared header click handling for the account and cart triggers.
 *
 * Account links ([data-clnf-account-link]) keep a real href (login or account page) as the fallback.
 * Retail: a click always opens the Inveterate account widget. If the widget is still loading, the
 * click waits for it (link gets .is-loading) and opens it once ready; if it isn't ready within
 * ACCOUNT_WIDGET_WAIT_MS, or loads disabled (no `body > .iaw`), the href navigates as normal.
 * The widget fires `iaw:ready` either way, but only appends `body > .iaw` when it is enabled for the shop.
 * Wholesale (business host): links always navigate to Shopify's native account pages.
 *
 * Cart links: when the native cart drawer is on the page (wholesale only), cart triggers
 * open it instead of navigating, and propagation is stopped so the Upnova cart builder's
 * listener on the same anchor never runs. Retail pages have no drawer, so Upnova or the
 * plain /cart href handle the click untouched.
 *
 * The file name must not contain "widget": the Inveterate loader reads its config from
 * the first `script[src*="widget"]` on the page.
 */
(() => {
  if (window.__clnfHeaderTriggersBound) return;
  window.__clnfHeaderTriggersBound = true;

  const ACCOUNT_WIDGET_WAIT_MS = 4000;
  const isB2bHost = window.location.hostname === 'business.countrylifefoods.com';

  /** @type {'loading' | 'ready' | 'unavailable'} */
  let accountWidgetState = 'loading';
  /** @type {{ link: HTMLAnchorElement, timer: number } | null} */
  let pendingAccountClick = null;

  const widgetInstance = () => {
    const instance = window.IAW?.Widget?.instance;
    return document.querySelector('body > .iaw') && typeof instance?.show === 'function' ? instance : null;
  };

  const closeMobileNavDrawer = () => {
    document.body.classList.remove('js-drawer-open', 'js-drawer-open-left');
    document.documentElement.classList.remove('js-drawer-open', 'js-drawer-open-left');
  };

  const openAccountWidget = () => {
    closeMobileNavDrawer();
    widgetInstance()?.show();
  };

  /** @param {boolean} openWidget - false sends the pending click on to its href. */
  const settlePendingAccountClick = (openWidget) => {
    if (!pendingAccountClick) return;
    const { link, timer } = pendingAccountClick;
    pendingAccountClick = null;
    window.clearTimeout(timer);
    link.classList.remove('is-loading');
    link.removeAttribute('aria-busy');
    if (openWidget) openAccountWidget();
    else window.location.assign(link.href);
  };

  const onWidgetReady = () => {
    accountWidgetState = widgetInstance() ? 'ready' : 'unavailable';
    settlePendingAccountClick(accountWidgetState === 'ready');
  };

  window.addEventListener('iaw:ready', onWidgetReady);
  // The widget script can finish before this deferred file binds; catch an earlier ready.
  if (widgetInstance()) accountWidgetState = 'ready';

  /** @param {MouseEvent} event */
  const isPlainClick = (event) =>
    !event.defaultPrevented && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;

  /** @param {Element} link */
  const isCartLink = (link) => {
    if (link.hasAttribute('data-clnf-cart-trigger')) return true;
    if (!(link instanceof HTMLAnchorElement) || !link.href) return false;
    return link.origin === window.location.origin && /\/cart\/?$/.test(link.pathname);
  };

  document.addEventListener(
    'click',
    (event) => {
      if (!isPlainClick(event) || !(event.target instanceof Element)) return;

      const accountLink = event.target.closest('a[data-clnf-account-link]');
      if (accountLink instanceof HTMLAnchorElement) {
        if (isB2bHost || accountWidgetState === 'unavailable') return;
        event.preventDefault();
        if (accountWidgetState === 'ready' || widgetInstance()) {
          accountWidgetState = 'ready';
          openAccountWidget();
          return;
        }
        if (pendingAccountClick) return;
        accountLink.classList.add('is-loading');
        accountLink.setAttribute('aria-busy', 'true');
        pendingAccountClick = {
          link: accountLink,
          timer: window.setTimeout(() => settlePendingAccountClick(Boolean(widgetInstance())), ACCOUNT_WIDGET_WAIT_MS),
        };
        return;
      }

      const link = event.target.closest('a[href], [data-clnf-cart-trigger]');
      if (!link || !isCartLink(link) || link.closest('cart-drawer-component')) return;

      const drawer = document.querySelector('cart-drawer-component');
      if (!drawer || typeof drawer.open !== 'function') return;

      event.preventDefault();
      event.stopPropagation();
      closeMobileNavDrawer();
      drawer.open();
    },
    true
  );
})();
